import { apiErrorMessage } from '@/utils/apiError';
import { IST_OFFSET_MS } from '@/utils/cricketCalendar';
import { createLatestGuard } from '@/utils/latestRequest';
import type {
  PvpAbortKind,
  PvpBallLogDto,
  PvpClientInfo,
  PvpCoinSource,
  PvpEndRequestDto,
  PvpEndReason,
  PvpInningsTotal,
  PvpMatchDetailDto,
  PvpMatchPlayerRef,
  PvpMatchRow,
  PvpMatchStatus,
  PvpMatchesDto,
  PvpMatchesQuery,
  PvpPlayerDto,
  PvpSide,
} from '@/types/pvp';

// The routes keep nulls, but every nullable field is still read with `== null`
// so a dropped key and a null look the same. No label ever shows NaN.

// ---- Filters and the query ----

export type MatchBots = 'include' | 'exclude' | 'only';

export const STATUS_OPTIONS: Array<{ label: string; value: PvpMatchStatus }> = [
  { label: 'Live', value: 'live' },
  { label: 'Ended', value: 'ended' },
  { label: 'Aborted', value: 'aborted' },
];

export const REASON_OPTIONS: Array<{ label: string; value: PvpEndReason }> = [
  { label: 'Completed', value: 'completed' },
  { label: 'Draw', value: 'draw' },
  { label: 'Forfeit', value: 'forfeit' },
  { label: 'Quit', value: 'quit' },
  { label: 'Disconnect', value: 'disconnect' },
  { label: 'Aborted by server', value: 'abort' },
];

export const BOTS_OPTIONS: Array<{ label: string; value: MatchBots }> = [
  { label: 'Include bots', value: 'include' },
  { label: 'Hide bots', value: 'exclude' },
  { label: 'Bots only', value: 'only' },
];

export const DEFAULT_RANGE_DAYS = 7;
export const MAX_RANGE_DAYS = 31;
export const MIN_Q = 2;
export const PAGE_LIMIT = 50;

const DAY_MS = 24 * 60 * 60 * 1000;

/** What the controls hold. `range` null = the last 7 days, counted back from the moment of the search. */
export interface MatchFilters {
  q: string;
  status?: PvpMatchStatus;
  reason?: PvpEndReason;
  bots: MatchBots;
  range: [string, string] | null;
}

export const DEFAULT_FILTERS: MatchFilters = { q: '', bots: 'include', range: null };

/** The filters a page link opens with: `?q=` pre-fills the search box. */
export function filtersFromSearch(search: string): MatchFilters {
  const q = new URLSearchParams(search).get('q')?.trim() ?? '';
  return { ...DEFAULT_FILTERS, q };
}

/** The range a search covers: the chosen one, or the last 7 days up to `now`. */
export function rangeFor(filters: MatchFilters, now: Date): { from: string; to: string } {
  if (filters.range) return { from: filters.range[0], to: filters.range[1] };
  return {
    from: new Date(now.getTime() - DEFAULT_RANGE_DAYS * DAY_MS).toISOString(),
    to: now.toISOString(),
  };
}

/** Why the search cannot run, or null. The server answers 400 for the same cases. */
export function filtersError(filters: MatchFilters, now: Date): string | null {
  const q = filters.q.trim();
  if (q !== '' && q.length < MIN_Q) return `Type at least ${MIN_Q} characters to search.`;
  const { from, to } = rangeFor(filters, now);
  const span = Date.parse(to) - Date.parse(from);
  if (!(span > 0)) return 'The range must start before it ends.';
  if (span > MAX_RANGE_DAYS * DAY_MS) return `The range can be at most ${MAX_RANGE_DAYS} days.`;
  return null;
}

export function matchesQuery(filters: MatchFilters, now: Date, cursor?: string): PvpMatchesQuery {
  const query: PvpMatchesQuery = {
    ...rangeFor(filters, now),
    bots: filters.bots,
    limit: PAGE_LIMIT,
  };
  const q = filters.q.trim();
  if (q) query.q = q;
  if (filters.status) query.status = filters.status;
  if (filters.reason) query.reason = filters.reason;
  if (cursor) query.cursor = cursor;
  return query;
}

/** The next page after the rows shown; a row the page repeats keeps its first copy. */
export function mergeRows(existing: PvpMatchRow[], incoming: PvpMatchRow[]): PvpMatchRow[] {
  const seen = new Set(existing.map((r) => r.id));
  const added = incoming.filter((r) => {
    if (seen.has(r.id)) return false;
    seen.add(r.id);
    return true;
  });
  return [...existing, ...added];
}

// ---- Labels ----

const DASH = '—';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** A number with Indian separators; "—" for null, missing or not finite. */
export function fmtNum(value: number | null | undefined, maxDigits = 2): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return DASH;
  return value.toLocaleString('en-IN', { maximumFractionDigits: maxDigits });
}

/** "30 Oct 11:49" in IST (with seconds on request); "—" for no or a bad time. */
export function whenLabel(iso: string | null | undefined, seconds = false): string {
  if (iso == null) return DASH;
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return DASH;
  const shifted = new Date(ms + IST_OFFSET_MS);
  const clock = shifted.toISOString().slice(11, seconds ? 19 : 16);
  return `${shifted.getUTCDate()} ${MONTHS[shifted.getUTCMonth()]} ${clock}`;
}

/** "45s", "3m 12s", "2m", "1h 2m"; "—" for no duration. */
export function fmtDuration(sec: number | null | undefined): string {
  if (typeof sec !== 'number' || !Number.isFinite(sec) || sec < 0) return DASH;
  const total = Math.round(sec);
  if (total < 60) return `${total}s`;
  if (total < 3600) {
    const m = Math.floor(total / 60);
    const s = total % 60;
    return s === 0 ? `${m}m` : `${m}m ${s}s`;
  }
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

/** The player's name; a bot by its name; a short id when there is no name. */
export function playerLabel(ref: PvpMatchPlayerRef | null | undefined): string {
  if (ref == null) return 'Unknown';
  if (ref.name) return ref.name;
  if (ref.bot) return 'Bot';
  return ref.userId ? ref.userId.slice(0, 8) : 'Unknown';
}

export const playersLabel = (row: Pick<PvpMatchRow, 'players'>): string =>
  `${playerLabel(row.players?.A)} vs ${playerLabel(row.players?.B)}`;

/** The winner's name, Draw, or a dash (live, aborted or no result). */
export function resultLabel(row: PvpMatchRow): string {
  if (row.winner === 'A' || row.winner === 'B') return playerLabel(row.players?.[row.winner]);
  if (row.reason === 'draw') return 'Draw';
  return DASH;
}

const REASON_WORDS: Record<PvpEndReason, string> = {
  completed: 'Completed',
  draw: 'Draw',
  forfeit: 'Forfeit',
  quit: 'Quit',
  disconnect: 'Disconnect',
  abort: 'Aborted by server',
};

export const reasonLabel = (reason: PvpEndReason | null | undefined): string =>
  reason == null ? DASH : (REASON_WORDS[reason] ?? reason);

/** Innings totals in innings order: "54/2 v 50/3"; "—" when there are none. */
export function scoreLabel(innings: PvpInningsTotal[] | null | undefined): string {
  if (!innings || innings.length === 0) return DASH;
  return [...innings]
    .sort((a, b) => a.inn - b.inn)
    .map((i) => `${fmtNum(i.runs, 0)}/${fmtNum(i.wkts, 0)}`)
    .join(' v ');
}

/** "180 / 0": what A and B were paid. */
export function payoutLabel(payout: { A: number; B: number } | null | undefined): string {
  if (payout == null) return DASH;
  return `${fmtNum(payout.A, 0)} / ${fmtNum(payout.B, 0)}`;
}

// ---- Match detail ----

const ABORT_WORDS: Record<PvpAbortKind, string> = {
  setup_mismatch: 'Setup mismatch: the phones did not agree on the match setup',
  core_mismatch: "Core mismatch: a phone's ball core differs from the server's",
  load_failure: 'Load failure: a phone could not load the match',
  stale_refund: 'Never settled: the reconcile sweep refunded the entry fees',
};

/** The abort cause in words, naming the side's phone; null for no cause. */
export function abortCauseText(
  cause: { kind: PvpAbortKind; side: PvpSide | null } | null | undefined,
  players: { A: PvpMatchPlayerRef; B: PvpMatchPlayerRef } | null | undefined
): string | null {
  if (cause == null) return null;
  const words = ABORT_WORDS[cause.kind] ?? cause.kind;
  if (cause.side === 'A' || cause.side === 'B') {
    return `${words} (${cause.side}, ${playerLabel(players?.[cause.side])}'s phone)`;
  }
  return cause.kind === 'load_failure' ? `${words} (both phones)` : words;
}

/** "1,200 → 1,216 (+16)"; "—" when there is no rating row. */
export function ratingChange(r: { old: number; new: number } | null | undefined): string {
  if (r == null) return DASH;
  const diff = r.new - r.old;
  const sign = diff > 0 ? '+' : '';
  return `${fmtNum(r.old, 0)} → ${fmtNum(r.new, 0)} (${sign}${fmtNum(diff, 0)})`;
}

/** "0.4.5 b57 android", leaving out what the phone did not say; "—" for none. */
export function clientLabel(client: PvpClientInfo | null | undefined): string {
  if (client == null) return DASH;
  const parts = [client.ver, client.bv == null ? null : `b${client.bv}`, client.platform].filter(
    (p): p is string => !!p
  );
  return parts.length === 0 ? DASH : parts.join(' ');
}

const COIN_SOURCE_WORDS: Record<PvpCoinSource, string> = {
  pvp_entry: 'Entry',
  pvp_payout: 'Payout',
  pvp_refund: 'Refund',
};

export interface CoinTableRow {
  key: string;
  side: PvpSide;
  player: string;
  source: string;
  amount: string;
  at: string;
}

/** The coins table, oldest first as the server sends it. */
export function coinRows(detail: Pick<PvpMatchDetailDto, 'coins' | 'players'>): CoinTableRow[] {
  return (detail.coins ?? []).map((c, i) => ({
    key: String(i),
    side: c.side,
    player: playerLabel(detail.players?.[c.side]),
    source: COIN_SOURCE_WORDS[c.source] ?? c.source,
    amount: `${c.amount > 0 ? '+' : ''}${fmtNum(c.amount, 0)}`,
    at: whenLabel(c.at, true),
  }));
}

// ---- Ball log ----

/** The line above the ball table: why there is no log, or that it is cut; null for a full log. */
export function ballLogNote(log: PvpBallLogDto): string | null {
  if (!log.available) return `Ball log not available: ${log.reason ?? 'unknown reason'}`;
  if (log.truncated) return 'Showing the first 600 balls; the match has more.';
  return null;
}

export interface BallTableRow {
  key: number;
  n: number;
  inn: number;
  ball: string;
  runs: string;
  extras: string;
  legal: string;
  wicket: string;
}

export function ballRows(log: PvpBallLogDto | null | undefined): BallTableRow[] {
  return (log?.balls ?? []).map((b) => ({
    key: b.n,
    n: b.n,
    inn: b.inn,
    ball: b.ball == null ? DASH : String(b.ball),
    runs: fmtNum(b.batRuns, 0),
    extras: fmtNum(b.extras, 0),
    legal: b.legal ? 'Yes' : 'No',
    wicket: b.wicket ? 'Wicket' : '',
  }));
}

// ---- Player ----

/** "3 of 10", or "3 (cap from server env)" when the stored config has no cap. */
export interface SyncTableRow {
  key: string;
  label: string;
  /** Match total; a dash for a per-phone line. */
  match: string;
  A: string;
  B: string;
}

const pick = (o: unknown, ...path: string[]): unknown =>
  path.reduce<unknown>(
    (v, k) =>
      v !== null && typeof v === 'object' && !Array.isArray(v)
        ? (v as Record<string, unknown>)[k]
        : undefined,
    o
  );
const count = (o: unknown, ...path: string[]): string => {
  const v = pick(o, ...path);
  return typeof v === 'number' ? fmtNum(v, 0) : DASH;
};

/**
 * The match's sync summary (backend pvp-sync-summary.ts, v1) as table lines: match totals, and per phone where the
 * summary splits it. Every field is read defensively: a summary of another shape shows dashes, never throws.
 */
export function syncRows(summary: Record<string, unknown> | null | undefined): SyncTableRow[] {
  const total = (key: string, label: string, ...path: string[]): SyncTableRow => ({
    key,
    label,
    match: count(summary, ...path),
    A: DASH,
    B: DASH,
  });
  const perSide = (key: string, label: string, ...path: string[]): SyncTableRow => ({
    key,
    label,
    match: DASH,
    A: count(summary, 'sides', 'A', ...path),
    B: count(summary, 'sides', 'B', ...path),
  });
  const both = (key: string, label: string, ...path: string[]): SyncTableRow => ({
    ...perSide(key, label, ...path),
    match: count(summary, ...path),
  });
  const net = (s: 'A' | 'B'): string => {
    const parts = ['good', 'fair', 'poor'].map((k) => pick(summary, 'sides', s, 'net', k));
    return parts.every((v) => typeof v === 'number')
      ? parts.map((v) => fmtNum(v as number, 0)).join(' / ')
      : DASH;
  };
  return [
    total('balls', 'Balls with a result', 'balls'),
    both('desync', 'Desync (a phone saw another outcome)', 'mismatch', 'desync'),
    both('inputs', 'Input mismatches', 'mismatch', 'inputs'),
    total('checkFailed', 'Ball checks failed', 'check', 'failed'),
    total('checkSplits', 'Ball checks split', 'check', 'splits'),
    total('abortSim', 'Aborts (pvp-sim)', 'aborts', 'sim'),
    total('abortLate', 'Aborts (late)', 'aborts', 'late'),
    perSide('stalls', 'Stalls', 'stalls'),
    perSide('cuts', 'Cut for silence', 'cuts'),
    perSide('resumes', 'Resumes', 'resumes'),
    {
      key: 'net',
      label: 'Net changes to good / fair / poor',
      match: DASH,
      A: net('A'),
      B: net('B'),
    },
  ];
}

export function paidTodayLabel(paid: number | null | undefined, cap: number | null | undefined) {
  const n = fmtNum(paid ?? 0, 0);
  return cap == null ? `${n} (cap from server env)` : `${n} of ${fmtNum(cap, 0)}`;
}

export function recordLine(rating: PvpPlayerDto['rating'] | undefined): string {
  if (rating == null) return 'Never played PvP';
  return `${fmtNum(rating.wins, 0)} W · ${fmtNum(rating.losses, 0)} L · ${fmtNum(rating.draws, 0)} D`;
}

// ---- Loading ----

export interface MatchesState {
  rows: PvpMatchRow[];
  nextCursor: string | null;
  matchedPlayers: PvpMatchesDto['matchedPlayers'];
  /** True once a search has answered or failed (the empty state shows only then). */
  searched: boolean;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
}

export const INITIAL_MATCHES: MatchesState = {
  rows: [],
  nextCursor: null,
  matchedPlayers: [],
  searched: false,
  loading: false,
  loadingMore: false,
  error: null,
};

type FetchMatches = (query: PvpMatchesQuery) => Promise<{ data: PvpMatchesDto }>;

/**
 * Searches and pages the match list. Load more repeats the search that was
 * answered, with the range the server answered (so "now" does not drift) and
 * the cursor. Only the newest call applies: a slow older read cannot overwrite
 * a newer search.
 */
export function createMatchesLoader(
  fetchMatches: FetchMatches,
  apply: (patch: Partial<MatchesState>) => void
) {
  const guard = createLatestGuard();
  let rows: PvpMatchRow[] = [];
  let cursor: string | null = null;
  let base: PvpMatchesQuery | null = null;

  return {
    async search(filters: MatchFilters, now: Date): Promise<void> {
      const isLatest = guard.begin();
      apply({ loading: true, loadingMore: false, error: null });
      const query = matchesQuery(filters, now);
      try {
        const { data } = await fetchMatches(query);
        if (!isLatest()) return;
        rows = data.rows ?? [];
        cursor = data.nextCursor ?? null;
        base = { ...query, from: data.from ?? query.from, to: data.to ?? query.to };
        apply({
          rows,
          nextCursor: cursor,
          matchedPlayers: data.matchedPlayers ?? [],
          searched: true,
          loading: false,
        });
      } catch (e) {
        if (!isLatest()) return;
        rows = [];
        cursor = null;
        base = null;
        apply({
          rows: [],
          nextCursor: null,
          matchedPlayers: [],
          searched: true,
          loading: false,
          error: apiErrorMessage(e, 'Failed to load PvP matches'),
        });
      }
    },

    async loadMore(): Promise<void> {
      if (cursor == null || base == null) return;
      const isLatest = guard.begin();
      apply({ loadingMore: true, error: null });
      try {
        const { data } = await fetchMatches({ ...base, cursor });
        if (!isLatest()) return;
        rows = mergeRows(rows, data.rows ?? []);
        cursor = data.nextCursor ?? null;
        apply({ rows, nextCursor: cursor, loadingMore: false });
      } catch (e) {
        if (!isLatest()) return;
        apply({ loadingMore: false, error: apiErrorMessage(e, 'Failed to load more matches') });
      }
    },
  };
}

export interface DrawerState {
  id: string | null;
  match: PvpMatchDetailDto | null;
  matchLoading: boolean;
  matchError: string | null;
  balls: PvpBallLogDto | null;
  ballsLoading: boolean;
}

export const CLOSED_DRAWER: DrawerState = {
  id: null,
  match: null,
  matchLoading: false,
  matchError: null,
  balls: null,
  ballsLoading: false,
};

/**
 * The match drawer: the match and its ball log are two calls started together
 * when the drawer opens, so a slow archive never holds the summary back. A
 * failed ball log call becomes an unavailable log with the reason.
 */
export function createDrawerLoader(
  fetchMatch: (id: string) => Promise<{ data: PvpMatchDetailDto }>,
  fetchBalls: (id: string) => Promise<{ data: PvpBallLogDto }>,
  apply: (patch: Partial<DrawerState>) => void
) {
  const guard = createLatestGuard();
  return {
    async open(id: string): Promise<void> {
      const isLatest = guard.begin();
      apply({
        id,
        match: null,
        matchLoading: true,
        matchError: null,
        balls: null,
        ballsLoading: true,
      });
      const match = fetchMatch(id).then(
        ({ data }) => {
          if (isLatest()) apply({ match: data, matchLoading: false });
        },
        (e) => {
          if (isLatest())
            apply({
              matchLoading: false,
              matchError: apiErrorMessage(e, 'Failed to load the match'),
            });
        }
      );
      const balls = fetchBalls(id).then(
        ({ data }) => {
          if (isLatest()) apply({ balls: data, ballsLoading: false });
        },
        (e) => {
          if (isLatest())
            apply({
              ballsLoading: false,
              balls: {
                available: false,
                source: null,
                reason: apiErrorMessage(e, 'the request failed'),
                truncated: false,
                balls: [],
              },
            });
        }
      );
      await Promise.all([match, balls]);
    },
    close(): void {
      guard.begin();
      apply({ ...CLOSED_DRAWER });
    },
  };
}

export interface PlayerState {
  id: string | null;
  player: PvpPlayerDto | null;
  loading: boolean;
  error: string | null;
}

export const CLOSED_PLAYER: PlayerState = { id: null, player: null, loading: false, error: null };

export function createPlayerLoader(
  fetchPlayer: (userId: string) => Promise<{ data: PvpPlayerDto }>,
  apply: (patch: Partial<PlayerState>) => void
) {
  const guard = createLatestGuard();
  return {
    async open(userId: string): Promise<void> {
      const isLatest = guard.begin();
      apply({ id: userId, player: null, loading: true, error: null });
      try {
        const { data } = await fetchPlayer(userId);
        if (isLatest()) apply({ player: data, loading: false });
      } catch (e) {
        if (isLatest())
          apply({ loading: false, error: apiErrorMessage(e, 'Failed to load the player') });
      }
    },
    close(): void {
      guard.begin();
      apply({ ...CLOSED_PLAYER });
    },
  };
}

// ---- Ending a stuck match (POST /admin/pvp/matches/:id/end, players:edit) ----

export const END_NOTE_MAX = 200;
/** How often an open drawer re-reads a match whose end request is still pending. */
export const END_POLL_MS = 10_000;

/** The drawer offers the end on a live match nobody has asked to end yet, or whose request failed (asked again). */
export function canEndMatch(m: PvpMatchDetailDto): boolean {
  return m.status === 'live' && (m.endRequest == null || m.endRequest.outcome === 'failed');
}

/** An end request a PvP server has not finished with. */
export function endRequestPending(m: PvpMatchDetailDto): boolean {
  return m.endRequest != null && m.endRequest.doneAt == null;
}

/** Why the note cannot be sent, or null. */
export function endNoteError(note: string): string | null {
  if (note.trim() === '') return 'Say why the match is being ended';
  if (note.length > END_NOTE_MAX) return `At most ${END_NOTE_MAX} characters`;
  return null;
}

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;

/** The end request in one line, with the alert type that fits it. */
export function endRequestLine(r: PvpEndRequestDto): {
  type: 'info' | 'success' | 'warning' | 'error';
  text: string;
} {
  const who = r.requestedBy ?? 'an admin';
  const why = r.note ? ` (${r.note})` : '';
  if (r.doneAt == null) {
    const asked = `End asked by ${who} at ${whenLabel(r.requestedAt, true)} IST${why}: waiting for a PvP server`;
    if (r.attempts === 0) return { type: 'info', text: `${asked}, usually within 10 s.` };
    const last = r.lastError ? ` (last: ${r.lastError})` : '';
    return {
      type: 'warning',
      text: `${asked}, ${plural(r.attempts, 'failed attempt')} so far${last}.`,
    };
  }
  const at = `${whenLabel(r.doneAt, true)} IST`;
  const asked = ` Asked by ${who}${why}.`;
  switch (r.outcome) {
    case 'ended':
      return {
        type: 'success',
        text: `Ended at ${at}: no-fault abort, both entry fees refunded.${asked}`,
      };
    case 'refunded':
      return {
        type: 'success',
        text: `Refunded at ${at}: the match state was gone, so both entry fees were paid back.${asked}`,
      };
    case 'already_over':
      return {
        type: 'info',
        text: `Already over at ${at}: it ended on its own first; nothing was changed.${asked}`,
      };
    default: {
      const last = r.lastError ? ` (last: ${r.lastError})` : '';
      return {
        type: 'error',
        text: `Failed after ${plural(r.attempts, 'attempt')}${last}. While the match is live you can ask again.${asked}`,
      };
    }
  }
}

/** Sends the end request; the request made, or the server's reason it was refused (404, 409...). */
export async function endMatchRequest(
  post: (id: string, body: { note: string }) => Promise<{ data: PvpEndRequestDto }>,
  id: string,
  note: string
): Promise<{ request: PvpEndRequestDto } | { error: string }> {
  try {
    const { data } = await post(id, { note: note.trim() });
    return { request: data };
  } catch (e) {
    return { error: apiErrorMessage(e, 'Failed to end the match') };
  }
}
